import { useState } from "react";
import { Mail } from "lucide-react";
import api from "../../api/api";
import { Panel, Pill, apiError, formatDate, useOrgStyles } from "../../components/org/orgUi";

/**
 * Invitations waiting for someone to sign up (coaches, and students emailed
 * before roster uploads replaced email imports). Each was emailed a link; an
 * invite expires 30 days after it was last sent, and Resend starts the 30 days over.
 * Copy link gives the same link to send yourself (our email can land in spam),
 * and also starts the 30 days over.
 */
export default function PendingInvites({ orgId, invites, intro, onChanged, onError }) {
    const s = useOrgStyles();
    const [resentId, setResentId] = useState(null);
    const [copiedId, setCopiedId] = useState(null);

    const resend = async (invite) => {
        try {
            await api.post(`/org/${orgId}/invites/${invite.InviteID}/resend`);
            setResentId(invite.InviteID);
            setTimeout(() => setResentId(null), 2000);
            onChanged();
        } catch (err) { onError(apiError(err)); }
    };

    const copyLink = async (invite) => {
        let link;
        try {
            ({ data: { link } } = await api.post(`/org/${orgId}/invites/${invite.InviteID}/link`));
        } catch (err) { onError(apiError(err)); return; }
        try {
            await navigator.clipboard.writeText(link);
            setCopiedId(invite.InviteID);
            setTimeout(() => setCopiedId(null), 2000);
        } catch {
            // Clipboard blocked (e.g. Safari after the request): show it to copy by hand
            window.prompt(`Copy ${invite.Email}'s invite link:`, link);
        }
        onChanged(); // its expiry moved
    };

    const cancel = async (invite) => {
        try {
            await api.delete(`/org/${orgId}/invites/${invite.InviteID}`);
            onChanged();
        } catch (err) { onError(apiError(err)); }
    };

    if (invites.length === 0) return null;
    // Coach invites have no program, so that column only shows when one does
    const showProgram = invites.some((i) => i.ProgramName);
    return (
        <Panel title={`Pending invites (${invites.length})`} padded={false}>
            <p className={`px-5 pt-4 text-sm ${s.muted}`}>{intro}</p>
            <div className="relative mt-2 overflow-x-auto">
                <table className="w-full">
                    <thead>
                        <tr className={`border-b ${s.dark ? "border-slate-700" : "border-slate-100"}`}>
                            <th className={s.th}>Email</th>
                            {showProgram && <th className={s.th}>Program</th>}
                            <th className={s.th}>Expires</th>
                            <th className={s.th}><span className="sr-only">Actions</span></th>
                        </tr>
                    </thead>
                    <tbody>
                        {invites.map((i) => (
                            <tr key={i.InviteID} className={`border-b last:border-0 ${s.row}`}>
                                <td className={s.td}>
                                    <span className="inline-flex items-center gap-2"><Mail size={14} className={s.muted} /> {i.Email}</span>
                                </td>
                                {showProgram && <td className={s.td}>{i.ProgramName ? <Pill>{i.ProgramName}</Pill> : <span className={s.muted}>—</span>}</td>}
                                <td className={`${s.td} whitespace-nowrap`}>
                                    {i.IsExpired
                                        ? <Pill tone="warning">Expired</Pill>
                                        : i.ExpiresOn ? formatDate(i.ExpiresOn) : <span className={s.muted}>—</span>}
                                </td>
                                <td className={`${s.td} text-right`}>
                                    <div className="flex items-center justify-end gap-2">
                                        <button type="button" className={s.btnSm} onClick={() => resend(i)} disabled={resentId === i.InviteID}>
                                            {resentId === i.InviteID ? "Sent" : "Resend"}
                                        </button>
                                        <button type="button" className={s.btnSm} onClick={() => copyLink(i)}
                                                aria-label={`Copy ${i.Email}'s invite link`}>
                                            {copiedId === i.InviteID ? "Copied" : "Copy link"}
                                        </button>
                                        <button type="button" className={s.btnDanger} onClick={() => cancel(i)}>Cancel</button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </Panel>
    );
}
